import { useMemo, useState } from 'react'
import {
  Alert,
  Card,
  Col,
  DatePicker,
  Progress,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  BarChartOutlined,
  DollarOutlined,
  DownloadOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import {
  MAX_RANGE_DAYS,
  REVENUE_DIMENSIONS,
  fetchAgentsReport,
  fetchDashboardReport,
  fetchDeviceReport,
  fetchRevenueReport,
  fetchTopConsumersReport,
  fetchTrafficReport,
  type AgentPerformance,
  type DeviceBreakdownRow,
  type DeviceRegionRow,
  type Granularity,
  type ReportRange,
  type RevenuePoint,
  type TopConsumer,
  type ReportExportKind,
  type RevenueDimension,
  type TrafficPoint,
  downloadReportExport,
} from '@/features/reports/reportsApi'
import { DELIVERY_METHODS, SUBSCRIBER_STATES } from '@/features/subscriber/subscriberApi'

import { defaultDayRange } from '@/shared/lib/dayRange'
import {
  formatBytes,
  formatNumber,
  getDisplayTimeZone,
  todayInTenantZone,
} from '@/shared/lib/format'
import { formatMoneyValue, money } from '@/shared/lib/money'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { ScopeHidden } from '@/shared/ui/ScopeHidden'
import { ServerExportButton } from '@/shared/ui/ServerExportButton'

const { RangePicker } = DatePicker

const ISO_DAY = 'YYYY-MM-DD'

/** "Top" ro'yxatidagi qatorlar soni — ekranga sig'adigan hajm */
const TOP_LIMIT = 10

/** Agent hisoboti — serverdagi `ReportLimits.MAX_TOP` bilan bir xil */
const AGENT_LIMIT = 100

const PRESETS = ['days7', 'days30', 'months12', 'custom'] as const
type Preset = (typeof PRESETS)[number]

/*
  ⚠️ `todayInTenantZone` bu yerda EDI va endi `shared/lib/format.ts` da.

  Sabab: aynan shu funksiya panelning yana o'n uch joyida kerak bo'ldi va
  har birida `dayjs()` ko'rinishida — ya'ni BRAUZER zonasida — takrorlangan
  edi. Bitta to'g'ri nusxa va o'n uchta noto'g'risi turgandan ko'ra, bitta
  umumiy manba yaxshiroq. U yerda sinov bilan qoplangan (`tests/timezone.mjs`
  §12–15).

  Bu yerdagi ishlatilishi ZAXIRA yo'l bo'lib qoladi: normal holatda kun
  serverning O'ZIDAN keladi (`dashboard.day`) — `anchorDay` ga qarang.
*/

/**
 * Tanlovdan sana oynasini quradi. Oyna oxiri HAR DOIM tenantning bugungi
 * kuni — "oxirgi 7 kun" degani aynan shu.
 *
 * ⚠️ Hech bir variant {@link MAX_RANGE_DAYS} dan uzun bo'lmasligi kerak:
 * server undan uzun oynani 400 bilan rad etadi ("Hisobot oynasi 366 kundan
 * uzun bo'la olmaydi"). "12 oy" shuning uchun `-11 oy` + oy boshi (eng yomon
 * holatda 365 kun), `-12 oy` emas.
 */
function rangeOf(preset: Preset, anchorDay: string, custom: readonly [Dayjs, Dayjs]): ReportRange {
  const anchor = dayjs(anchorDay, ISO_DAY)
  switch (preset) {
    case 'days7':
      return { from: anchor.subtract(6, 'day').format(ISO_DAY), to: anchorDay }
    case 'days30':
      return { from: anchor.subtract(29, 'day').format(ISO_DAY), to: anchorDay }
    case 'months12':
      return { from: anchor.subtract(11, 'month').startOf('month').format(ISO_DAY), to: anchorDay }
    case 'custom':
      return { from: custom[0].format(ISO_DAY), to: custom[1].format(ISO_DAY) }
  }
}

/**
 * Qatorning ulushini ko'rsatuvchi ingichka chiziq.
 *
 * NEGA GRAFIK KUTUBXONASI YO'Q: loyihaning bog'liqliklarida birorta chizish
 * kutubxonasi yo'q (`package.json`), va uni bitta sahifa uchun qo'shish
 * ALOHIDA qaror — build hajmi, litsenziya, yangilanish zanjiri. Nisbatni
 * ko'rsatish uchun esa jadval ichidagi shu chiziq yetarli: eng katta qator
 * 100%, qolganlari unga nisbatan.
 */
function ShareBar({ value, max }: { value: number; max: number }) {
  if (max <= 0) return null
  return (
    <Progress
      percent={Math.round((value / max) * 100)}
      showInfo={false}
      size="small"
      style={{ margin: 0, lineHeight: 1 }}
    />
  )
}

/**
 * Kesim ro'yxati (tur / holat bo'yicha) — jadval emas, nisbat chiziqlari.
 *
 * ⚠️ `bucket` ERKIN SATR: server yangi tur yoki holat qo'shsa panel uni tanimay
 * qolishi mumkin. Shuning uchun tarjima `defaultValue` bilan so'raladi va
 * noma'lum kalit XOM HOLDA chiziladi — yashirilmaydi, aks holda yig'indi
 * ustunlar bilan mos kelmay qolardi va sababi ko'rinmasdi.
 */
function DeviceBreakdown({
  rows,
  prefix,
  emptyText,
}: {
  rows: readonly DeviceBreakdownRow[]
  prefix: string
  emptyText: string
}) {
  const { t } = useTranslation()
  if (rows.length === 0) {
    // ⚠️ `display: block` — sarlavha va matn YONMA-YON tushib qolardi
    // ("By typeNo data"), chunki `Typography.Text` — satr ichidagi element.
    return (
      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
        {emptyText}
      </Typography.Text>
    )
  }
  const max = Math.max(0, ...rows.map((row) => row.count))
  return (
    <div>
      {rows.map((row) => (
        <div key={row.bucket} style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <Typography.Text>
              {t(`${prefix}.${row.bucket}`, { defaultValue: row.bucket })}
            </Typography.Text>
            <Typography.Text strong>{formatNumber(row.count)}</Typography.Text>
          </div>
          <ShareBar value={row.count} max={max} />
        </div>
      ))}
    </div>
  )
}

/** Bo'lim sarlavhasi ostidagi bir qatorli izoh */
function CardSubtitle({ text }: { text: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {text}
      </Typography.Text>
    </div>
  )
}

/**
 * Bo'lim xatosi.
 *
 * Sahifaning O'ZI yiqilmaydi: har bo'lim mustaqil so'raydi va faqat
 * xato bergani ogohlantirish ko'rsatadi (`LedgerPage` bilan bir xil andoza).
 */
function SectionError({ error }: { error: unknown }) {
  const { t } = useTranslation()
  return (
    <Alert
      type="error"
      showIcon
      message={t('dataTable.loadFailed')}
      description={translateError(error, t)}
    />
  )
}

interface ExportButtonProps {
  kind: ReportExportKind
  range: ReportRange
  granularity?: Granularity
  limit?: number
  /** Ekranda qator bormi — bo'sh bo'lsa tugma chizilmaydi */
  hasRows: boolean
  enabled: boolean
  /** Shu filtr bilan kutilayotgan qator soni — chegarani oldindan aytish uchun */
  estimatedRows?: number | null
  dimension?: RevenueDimension
  state?: string | null
  subscriberKind?: string | null
}

/**
 * Eksport tugmasi — `report.export` huquqi bo'lsa VA ko'rsatiladigan
 * qator bo'lsa. Bo'sh faylni yuklab olish foydasiz va "ma'lumot bor edi"
 * degan yolg'on taassurot qoldiradi.
 *
 * ⚠️ FAYL SERVERDA YASALADI (`GET /api/reports/export/{kind}`).
 *
 * Ilgari u brauzerda, allaqachon yuklangan sahifadan yasalardi. Ikkita
 * oqibati bor edi va ikkalasi ham jimgina:
 *
 *   1) `report.export` huquqi AMALDA hech narsani boshqarmasdi — tugma
 *      yashirilardi, lekin ma'lumot brauzerda edi. Ya'ni huquq katalogda
 *      bor, majburlanmagan holatda edi.
 *   2) Eksport EKRANDAGI qator bilan cheklanardi: "yillik hisobotni
 *      Excel'ga chiqar" degan asosiy stsenariy ishlamasdi.
 *
 * ⚠️ 2026-08-14: FORMAT TANLANADI. Dvigatel CSV, XLSX va JSON ni ayni
 * kod bilan beradi, panel esa doim CSV so'rardi — Excel fayli mavjud
 * bo'la turib operatorga yetib bormasdi. Tanlash, chegara ogohlantirishi,
 * jarayon va PDF sababi `ServerExportButton` da, BITTA joyda.
 *
 * Fayl nomi `Content-Disposition` dan olinadi — davr serverda bir marta
 * hal qilinadi va barcha mijozlar bir xil nom oladi.
 */
function ExportButton({
  kind,
  range,
  granularity,
  limit,
  hasRows,
  enabled,
  estimatedRows = null,
  dimension,
  state,
  subscriberKind,
}: ExportButtonProps) {
  const { t } = useTranslation()

  if (!enabled || !hasRows) return null

  return (
    <ServerExportButton
      label={t('export.download')}
      title={t('export.menuTitle')}
      estimatedRows={estimatedRows}
      run={(format, onProgress) =>
        downloadReportExport(kind, format, {
          // ⚠️ `devices` sana oynasini OLMAYDI (joriy holat, vaqt qatori
          // emas) — server uni e'tiborsiz qoldiradi, lekin yuborilgani
          // ham zarar qilmaydi va shart bitta joyda qolmaydi.
          from: range.from,
          to: range.to,
          granularity,
          limit,
          dimension,
          state,
          subscriberKind,
          onProgress,
        })
      }
    />
  )
}

/**
 * Hisobotlar sahifasi — `xsib-report` mikroservisining beshta endpointi.
 *
 * ⚠️ UCH TAMOYIL, uchalasi ham jonli tizimda tekshirilgan:
 *
 * 1. KUN CHEGARASINI FRONTEND HISOBLAMAYDI. Oyna oxiri serverning o'zi
 *    aytgan kunga bog'lanadi (`dashboard.day`, tenant zonasida) va u ekranda
 *    ZONASI BILAN ko'rsatiladi. Shunda "hisobotda 3 GB, ro'yxatda 2.4 GB"
 *    degan chalkashlikning sababi darhol ko'rinadi. Davr yorliqlari
 *    (`label`) ham serverdan TAYYOR keladi va qayta formatlanmaydi.
 *
 * 2. HAR BO'LIM MUSTAQIL SO'RAYDI: biri xato bersa faqat o'sha bo'limda
 *    ogohlantirish chiqadi, qolganlari ishlashda davom etadi.
 *
 * 3. BO'SH JAVOB — XATO EMAS. Reseller daromadni ko'rmaydi va server unga
 *    403 emas, BO'SH RO'YXAT qaytaradi (`SubscriberReportResource`
 *    javadoc'i: "ruxsat yo'q" javobi ham ma'lumot bo'lardi). Shuning uchun
 *    har jadvalda `EmptyState` bor va u nima uchun bo'shligini aytadi.
 */
export function ReportsPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()

  const canExport = can('report.export')
  const canOpenSubscriber = can('subscriber.read')

  const [preset, setPreset] = useState<Preset>('days30')
  const [granularity, setGranularity] = useState<Granularity>('DAY')
  // ⚠️ Boshlang'ich qiymat TENANT kalendarida: `dayjs()` brauzernikini
  // olardi va "O'zim tanlayman" ga o'tilganda oyna jimgina bir kunga
  // surilgan holda ochilardi (`dayRange.ts`).
  const [custom, setCustom] = useState<[Dayjs, Dayjs]>(() => defaultDayRange(29))

  // Xulosa — sana parametrisiz. U ham KPI beradi, ham oyna uchun "bugun"ni.
  const summary = useQuery({
    queryKey: queryKeys.reports.dashboard,
    queryFn: fetchDashboardReport,
  })

  // Serverning kuni USTUN; u hali kelmagan yoki xato bergan bo'lsa — tenant
  // zonasidagi bugun. Ikkalasi ham BIR manbadan (tenant zonasi) chiqadi,
  // shuning uchun amalda farq qilmaydi, lekin serverniki tekshirilgan.
  const anchorDay = summary.data?.day ?? todayInTenantZone()
  const dayZone = summary.data?.dayTimezone ?? getDisplayTimeZone()

  const range = useMemo(() => rangeOf(preset, anchorDay, custom), [preset, anchorDay, custom])

  const traffic = useQuery({
    queryKey: queryKeys.reports.traffic(range.from, range.to, granularity),
    queryFn: () => fetchTrafficReport(range, granularity),
  })
  const revenue = useQuery({
    queryKey: queryKeys.reports.revenue(range.from, range.to, granularity),
    queryFn: () => fetchRevenueReport(range, granularity),
  })
  const top = useQuery({
    queryKey: queryKeys.reports.topConsumers(range.from, range.to, TOP_LIMIT),
    queryFn: () => fetchTopConsumersReport(range, TOP_LIMIT),
  })
  const agents = useQuery({
    queryKey: queryKeys.reports.agents(range.from, range.to, AGENT_LIMIT),
    queryFn: () => fetchAgentsReport(range, AGENT_LIMIT),
  })
  // ⚠️ Davr parametri YO'Q: qurilmalar hisoboti JORIY holatni sanaydi, oyna
  // ichidagi iste'molni emas (`reportsApi.fetchDeviceReport` izohi). Shuning
  // uchun u `range` o'zgarganda qayta so'ralmaydi.
  const devices = useQuery({
    queryKey: queryKeys.reports.devices,
    queryFn: fetchDeviceReport,
  })

  const trafficRows = traffic.data ?? []
  const revenueRows = revenue.data ?? []
  const topRows = top.data ?? []
  /**
   * ⚠️ FAOLIYATSIZ AGENTLAR RO'YXATDAN CHIQARILADI.
   *
   * Server oynadagi BARCHA agentlarni qaytaradi, shu jumladan yopilganlarini
   * va davr ichida hech nima sotmaganlarini. Staging'da bu 60 dan ortiq
   * qator degani edi va ularning hammasida "0 AFN / 0 AFN" turardi:
   * hisobotning ma'noli qismi shovqin ichida ko'rinmasdi.
   *
   * ⚠️ "Sotuvi nol" — bu MA'LUMOT, va u butunlay yo'qolmaydi: nechtasi
   * yashirilgani jadval ostida yoziladi. Yashirilmasa esa hisobot
   * o'qib bo'lmaydigan holga kelardi.
   */
  const allAgentRows = agents.data ?? []
  const agentRows = allAgentRows.filter(
    (row) => row.sold.amount !== 0 || row.commission.amount !== 0,
  )
  const idleAgents = allAgentRows.length - agentRows.length

  const trafficMax = Math.max(0, ...trafficRows.map((r) => r.totalBytes))
  const topMax = Math.max(0, ...topRows.map((r) => r.totalBytes))
  const revenueMax = Math.max(0, ...revenueRows.map((r) => r.revenue.amount))

  const trafficTotals = trafficRows.reduce(
    (acc, row) => ({
      upload: acc.upload + row.uploadBytes,
      download: acc.download + row.downloadBytes,
      total: acc.total + row.totalBytes,
      free: acc.free + row.freeBytes,
    }),
    { upload: 0, download: 0, total: 0, free: 0 },
  )

  // ⚠️ Summani `addMoney` bilan yig'maymiz: u valyuta mos kelmasa ISTISNO
  // tashlaydi va butun sahifani yiqitardi. Bitta tenant = bitta valyuta
  // (`money.ts`), ya'ni bu amalda bo'lmaydi — lekin hisobotni bitta
  // kutilmagan qatorga bog'lab qo'yish keraksiz xavf.
  const revenueTotal = revenueRows.reduce((sum, row) => sum + row.revenue.amount, 0)
  // ⚠️ `summary.data.revenue` endi NULLABLE: doira daromadni qamramasa
  // backend uni `null` qaytaradi (`DashboardSummary`). Zaxira zanjiri shu
  // sababdan `?.` bilan.
  const revenueCurrency = revenueRows[0]?.revenue.currency ?? summary.data?.revenue?.currency ?? ''
  const revenueTxns = revenueRows.reduce((sum, row) => sum + row.txnCount, 0)

  const changePreset = (next: Preset) => {
    setPreset(next)
    // Qadam tanlovga ERGASHADI: 12 oylik oynani kunlar bo'yicha ko'rsatish
    // 365 qatorli jadval degani. Foydalanuvchi keyin qo'lda o'zgartira oladi.
    setGranularity(next === 'months12' ? 'MONTH' : 'DAY')
  }

  /** Kelajakdagi va {@link MAX_RANGE_DAYS} dan uzoqroq sanani tanlab bo'lmaydi */
  const disabledDate = (current: Dayjs) => {
    const anchor = dayjs(anchorDay, ISO_DAY)
    if (current.isAfter(anchor, 'day')) return true
    return current.isBefore(anchor.subtract(MAX_RANGE_DAYS - 1, 'day'), 'day')
  }

  const periodTitle = t('reports.columns.period')
  const freeTitle = (
    <Tooltip title={t('reports.columns.freeHint')}>
      <span>{t('reports.columns.free')}</span>
    </Tooltip>
  )

  const trafficColumns: ColumnsType<TrafficPoint> = [
    // ⚠️ `label` SERVERDAN tayyor keladi va qayta formatlanmaydi — u tenant
    // zonasidagi davr. `new Date(label)` bilan chizish kunni surib yuborardi.
    { key: 'label', title: periodTitle, dataIndex: 'label', width: 130 },
    {
      key: 'upload',
      title: (
        <Tooltip title={t('terms.upload')}>
          <span>{t('reports.columns.upload')}</span>
        </Tooltip>
      ),
      align: 'right',
      width: 130,
      render: (_, row) => formatBytes(row.uploadBytes),
    },
    {
      key: 'download',
      title: (
        <Tooltip title={t('terms.download')}>
          <span>{t('reports.columns.download')}</span>
        </Tooltip>
      ),
      align: 'right',
      width: 130,
      render: (_, row) => formatBytes(row.downloadBytes),
    },
    {
      key: 'total',
      title: t('reports.columns.total'),
      align: 'right',
      width: 150,
      render: (_, row) => (
        <div>
          <Typography.Text strong>{formatBytes(row.totalBytes)}</Typography.Text>
          <ShareBar value={row.totalBytes} max={trafficMax} />
        </div>
      ),
    },
    {
      key: 'free',
      title: freeTitle,
      align: 'right',
      width: 130,
      render: (_, row) => formatBytes(row.freeBytes),
    },
    {
      key: 'subscribers',
      title: t('reports.columns.subscribers'),
      align: 'right',
      width: 120,
      render: (_, row) => formatNumber(row.subscriberCount),
    },
  ]

  const revenueColumns: ColumnsType<RevenuePoint> = [
    { key: 'label', title: periodTitle, dataIndex: 'label', width: 130 },
    {
      key: 'revenue',
      title: t('reports.columns.revenue'),
      align: 'right',
      render: (_, row) => (
        <div>
          <Typography.Text strong>{formatMoneyValue(row.revenue)}</Typography.Text>
          <ShareBar value={row.revenue.amount} max={revenueMax} />
        </div>
      ),
    },
    {
      key: 'txnCount',
      title: t('reports.columns.sales'),
      align: 'right',
      width: 120,
      render: (_, row) => formatNumber(row.txnCount),
    },
  ]

  const topColumns: ColumnsType<TopConsumer> = [
    {
      key: 'login',
      title: t('reports.top.subscriber'),
      // Havola FAQAT abonentni ko'rish huquqi bo'lsa: aks holda bosilgan
      // havola kartochkada "ruxsat yo'q" ekraniga olib borardi.
      render: (_, row) =>
        canOpenSubscriber ? (
          <Link to={`/subscribers/${row.subscriberId}`}>{row.login}</Link>
        ) : (
          <Typography.Text>{row.login}</Typography.Text>
        ),
    },
    {
      key: 'upload',
      title: t('reports.columns.upload'),
      align: 'right',
      width: 120,
      render: (_, row) => formatBytes(row.uploadBytes),
    },
    {
      key: 'download',
      title: t('reports.columns.download'),
      align: 'right',
      width: 120,
      render: (_, row) => formatBytes(row.downloadBytes),
    },
    {
      key: 'total',
      title: t('reports.columns.total'),
      align: 'right',
      width: 150,
      render: (_, row) => (
        <div>
          <Typography.Text strong>{formatBytes(row.totalBytes)}</Typography.Text>
          <ShareBar value={row.totalBytes} max={topMax} />
        </div>
      ),
    },
    {
      key: 'free',
      title: freeTitle,
      align: 'right',
      width: 120,
      render: (_, row) => formatBytes(row.freeBytes),
    },
  ]

  const agentColumns: ColumnsType<AgentPerformance> = [
    {
      key: 'agent',
      title: t('nav.agents'),
      render: (_, row) => (
        <Space size={4} wrap>
          <Typography.Text strong>{row.displayName}</Typography.Text>
          <Typography.Text type="secondary">{row.code}</Typography.Text>
        </Space>
      ),
    },
    {
      key: 'settlement',
      title: t('agents.settlementMode'),
      width: 120,
      render: (_, row) => (
        <Tag>{t(`settlementMode.${row.settlementMode}`, { defaultValue: row.settlementMode })}</Tag>
      ),
    },
    {
      key: 'sold',
      title: t('reports.agents.sold'),
      align: 'right',
      width: 130,
      render: (_, row) => formatMoneyValue(row.sold),
    },
    {
      key: 'commission',
      title: t('reports.agents.commission'),
      align: 'right',
      width: 130,
      render: (_, row) => formatMoneyValue(row.commission),
    },
    {
      // ⚠️ Qoldiq — JORIY holat, davr yig'indisi EMAS. Sarlavhadagi tooltip
      // aynan shuni aytadi: usiz u ham "davr bo'yicha" deb o'qilardi va
      // agent bilan hisob-kitob noto'g'ri chiqardi.
      key: 'balance',
      title: (
        <Tooltip title={t('reports.agents.balanceHint')}>
          <span>{t('common.balance')}</span>
        </Tooltip>
      ),
      align: 'right',
      width: 130,
      render: (_, row) => (
        <Typography.Text type={row.balance.amount < 0 ? 'danger' : undefined}>
          {formatMoneyValue(row.balance)}
        </Typography.Text>
      ),
    },
    {
      key: 'saleCount',
      title: t('reports.columns.sales'),
      align: 'right',
      width: 100,
      render: (_, row) => formatNumber(row.saleCount),
    },
  ]

  const deviceRows = devices.data?.byRegion ?? []
  const deviceMax = Math.max(0, ...deviceRows.map((row) => row.deviceCount))
  /**
   * ⚠️ SANOQ `…Count` MAYDONIDAN, ro'yxat UZUNLIGIDAN EMAS.
   *
   * Backend ro'yxatni `LIMIT` bilan kesadi, to'liq sonni esa alohida
   * beradi (`Unplaced` javadoc'i). `nas.length` ni ishlatish 200 ta
   * joylashuvsiz qurilmani "20 ta" deb ko'rsatardi, ya'ni aynan shu
   * ko'rsatkich o'lchayotgan narsani — ma'lumot to'liqligini — yashirardi.
   */
  const unplacedNas = devices.data?.unplaced.nasCount ?? 0
  const unplacedEquipment = devices.data?.unplaced.equipmentCount ?? 0
  const unplacedTotal = unplacedNas + unplacedEquipment
  /** Ro'yxatning O'ZI — "qaysi qurilma" degan savolga javob (kesilgan bo'lishi mumkin) */
  const unplacedDevices = [
    ...(devices.data?.unplaced.nas ?? []).map((d) => ({ ...d, kind: 'NAS' as const })),
    ...(devices.data?.unplaced.equipment ?? []).map((d) => ({ ...d, kind: 'EQUIPMENT' as const })),
  ]

  const deviceColumns: ColumnsType<DeviceRegionRow> = [
    { key: 'region', title: t('sites.region'), dataIndex: 'region', width: 180 },
    {
      key: 'siteCount',
      title: t('reports.devices.sites'),
      align: 'right',
      width: 120,
      render: (_, row) => formatNumber(row.siteCount),
    },
    {
      key: 'nasCount',
      title: t('nav.nas'),
      align: 'right',
      width: 120,
      render: (_, row) => formatNumber(row.nasCount),
    },
    {
      key: 'equipmentCount',
      title: t('reports.devices.equipment'),
      align: 'right',
      width: 130,
      render: (_, row) => formatNumber(row.equipmentCount),
    },
    {
      key: 'deviceCount',
      title: t('reports.devices.total'),
      align: 'right',
      width: 150,
      render: (_, row) => (
        <div>
          <Typography.Text strong>{formatNumber(row.deviceCount)}</Typography.Text>
          <ShareBar value={row.deviceCount} max={deviceMax} />
        </div>
      ),
    },
  ]

  // ⚠️ CSV USTUN TA'RIFLARI OLIB TASHLANDI.
  //
  // Ilgari bu yerda beshta `CsvColumn[]` massivi turardi va eksport
  // brauzerda, ekrandagi qatorlardan yasalardi. Endi fayl SERVERDA
  // yasaladi (`GET /api/reports/export/{kind}`) — ustunlar, sarlavhalar
  // va pul o'girish `ReportExportService` da, BITTA joyda.
  //
  // Ikki nusxa qoldirilmadi: ular bir kuni ajralib ketardi va ekrandagi
  // son bilan fayldagi son farq qilardi — bu esa hisobotga bo'lgan
  // ishonchni butunlay yo'qotadi.

  const emptyLocale = (title: string, description: string) => ({
    emptyText: <EmptyState icon={<BarChartOutlined />} title={title} description={description} />,
  })

  return (
    <>
      <PageHeader title={t('nav.reports')} description={t('reports.intro')} />

      {/* --- Davr tanlash. Oyna oxiri DOIM tenantning bugungi kuni --- */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle" align="center">
          <Space size={4} align="center" wrap>
            <Typography.Text type="secondary">{t('reports.rangeLabel')}:</Typography.Text>
            <Segmented<Preset>
              value={preset}
              onChange={changePreset}
              options={PRESETS.map((value) => ({
                value,
                label: value === 'custom' ? t('reports.rangeCustom') : t(`reports.range.${value}`),
              }))}
            />
          </Space>

          {preset === 'custom' ? (
            <RangePicker
              value={custom}
              onChange={(value) => {
                if (value?.[0] && value[1]) setCustom([value[0], value[1]])
              }}
              disabledDate={disabledDate}
              allowClear={false}
              format="DD.MM.YYYY"
            />
          ) : null}

          <Space size={4} align="center" wrap>
            <Typography.Text type="secondary">{t('reports.granularityLabel')}:</Typography.Text>
            <Segmented<Granularity>
              value={granularity}
              onChange={setGranularity}
              options={[
                { value: 'DAY', label: t('reports.byDay') },
                { value: 'MONTH', label: t('reports.byMonth') },
              ]}
            />
          </Space>
        </Space>

        {/*
          ⚠️ QAYSI ORALIQ VA QAYSI ZONA — EKRANDA. Bu bezak emas: kun
          chegarasi tenant zonasida hisoblanadi va son o'z-o'zidan tekshirib
          bo'lmaydigan qiymat. Uni ko'rsatmaslik MVP'dagi "ertalab kechagi
          trafik ko'rinyapti" holatini qaytarardi.
        */}
        <div style={{ marginTop: 8 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('reports.windowNote', { from: range.from, to: range.to })} ·{' '}
            {t('reports.dayZoneNote', { zone: dayZone })} ·{' '}
            {t('reports.rangeLimitNote', { days: MAX_RANGE_DAYS })}
          </Typography.Text>
        </div>
      </Card>

      {/* --- Bugungi xulosa (dashboard endpointi) --- */}
      {summary.isError ? (
        <div style={{ marginBottom: 16 }}>
          <SectionError error={summary.error} />
        </div>
      ) : (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {/*
            ⚠️ `null` — «doiradan tashqarida», NOL EMAS. Bu ikkala karta
            bosh sahifadagi kartalar bilan AYNI so'rovdan
            (`GET /api/reports/dashboard`) oziqlanadi va u yerda `null`
            `ScopeHidden` («—» + sabab) bilan chiziladi. `?? 0` shu yerda
            bir xil foydalanuvchiga bosh sahifada «Onlayn: —», hisobotlarda
            «Onlayn: 0» ko'rsatardi — ikkinchisi AGENT roli uchun ochiq
            yolg'on. Yonidagi trafik va daromad kartalari `null` ni
            allaqachon to'g'ri o'qiydi (`formatBytes`/`formatMoneyValue`).
          */}
          <Col xs={24} sm={12} xl={6}>
            <Card size="small" loading={summary.isPending}>
              <Statistic
                title={t('reports.today.online')}
                value={summary.data?.onlineSubscribers ?? undefined}
                formatter={(v) =>
                  v === undefined ? <ScopeHidden hint={t('dashboard.scopeHiddenHint')} /> : String(v)
                }
                prefix={<ThunderboltOutlined style={{ color: '#16a34a' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card size="small" loading={summary.isPending}>
              <Statistic
                title={t('reports.today.subscribers')}
                value={summary.data?.totalSubscribers ?? undefined}
                formatter={(v) =>
                  v === undefined ? <ScopeHidden hint={t('dashboard.scopeHiddenHint')} /> : String(v)
                }
                prefix={<TeamOutlined style={{ color: '#2563eb' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card size="small" loading={summary.isPending}>
              <Statistic
                title={t('reports.today.traffic')}
                value={formatBytes(summary.data?.totalBytes)}
                prefix={<BarChartOutlined style={{ color: '#0891b2' }} />}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                <UploadOutlined /> {formatBytes(summary.data?.uploadBytes)} ·{' '}
                <DownloadOutlined /> {formatBytes(summary.data?.downloadBytes)}
              </Typography.Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card size="small" loading={summary.isPending}>
              <Statistic
                title={t('reports.today.revenue')}
                value={formatMoneyValue(summary.data?.revenue)}
                prefix={<DollarOutlined style={{ color: '#16a34a' }} />}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('reports.today.day', { day: summary.data?.day ?? anchorDay, zone: dayZone })}
              </Typography.Text>
            </Card>
          </Col>
        </Row>
      )}

      {/* --- Trafik --- */}
      <Card
        title={t('reports.traffic.title')}
        style={{ marginBottom: 16 }}
        extra={
          <ExportButton
            kind="traffic"
            range={range}
            granularity={granularity}
            hasRows={trafficRows.length > 0}
            enabled={canExport}
          />
        }
      >
        <CardSubtitle
          text={`${t('reports.traffic.subtitle')} · ${
            granularity === 'DAY' ? t('reports.byDay') : t('reports.byMonth')
          }`}
        />

        {traffic.isError ? (
          <SectionError error={traffic.error} />
        ) : (
          <>
            <Row gutter={[16, 8]} style={{ marginBottom: 12 }}>
              <Col xs={12} md={6}>
                <Statistic
                  title={t('reports.columns.upload')}
                  value={formatBytes(trafficTotals.upload)}
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title={t('reports.columns.download')}
                  value={formatBytes(trafficTotals.download)}
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title={t('reports.traffic.total')}
                  value={formatBytes(trafficTotals.total)}
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title={t('reports.columns.free')}
                  value={formatBytes(trafficTotals.free)}
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
            </Row>

            <Table<TrafficPoint>
              columns={trafficColumns}
              dataSource={trafficRows}
              rowKey={(row) => row.periodStart}
              loading={traffic.isPending}
              size="small"
              pagination={false}
              scroll={{ x: 830, y: 360 }}
              locale={emptyLocale(t('reports.noData'), t('reports.noDataHint'))}
            />

            <Typography.Paragraph
              type="secondary"
              style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}
            >
              {t('reports.sourceWarning')}
            </Typography.Paragraph>
          </>
        )}
      </Card>

      {/* --- Daromad --- */}
      <Card
        title={t('reports.revenue.title')}
        style={{ marginBottom: 16 }}
        extra={
          <ExportButton
            kind="revenue"
            range={range}
            granularity={granularity}
            hasRows={revenueRows.length > 0}
            enabled={canExport}
          />
        }
      >
        <CardSubtitle text={t('reports.revenue.ledgerNote')} />

        {revenue.isError ? (
          <SectionError error={revenue.error} />
        ) : (
          <>
            <Row gutter={[16, 8]} style={{ marginBottom: 12 }}>
              <Col xs={12} md={12}>
                <Statistic
                  title={t('reports.revenue.totalMoney')}
                  value={
                    revenueCurrency ? formatMoneyValue(money(revenueTotal, revenueCurrency)) : '—'
                  }
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
              <Col xs={12} md={12}>
                <Statistic
                  title={t('reports.revenue.totalCount')}
                  value={formatNumber(revenueTxns)}
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
            </Row>

            <Table<RevenuePoint>
              columns={revenueColumns}
              dataSource={revenueRows}
              rowKey={(row) => row.periodStart}
              loading={revenue.isPending}
              size="small"
              pagination={false}
              scroll={{ x: 480, y: 360 }}
              locale={emptyLocale(t('reports.revenue.empty'), t('reports.revenue.emptyHint'))}
            />
          </>
        )}
      </Card>

      <Row gutter={[16, 16]}>
        {/* --- Top iste'molchilar --- */}
        <Col xs={24} xl={12}>
          <Card
            title={t('reports.top.title')}
            extra={
              <ExportButton
                kind="top-consumers"
                range={range}
                limit={TOP_LIMIT}
                hasRows={topRows.length > 0}
                enabled={canExport}
              />
            }
          >
            <CardSubtitle text={t('reports.top.subtitle', { n: TOP_LIMIT })} />

            {top.isError ? (
              <SectionError error={top.error} />
            ) : (
              <Table<TopConsumer>
                columns={topColumns}
                dataSource={topRows}
                rowKey={(row) => row.subscriberId}
                loading={top.isPending}
                size="small"
                pagination={false}
                scroll={{ x: 680 }}
                locale={emptyLocale(t('reports.top.empty'), t('reports.top.emptyHint'))}
              />
            )}
          </Card>
        </Col>

        {/* --- Agentlar.
            ⚠️ ALOHIDA `agent.read` TALAB QILINMAYDI va bu ataylab: server bu
            hisobotni FAQAT `report.read` bilan beradi (`AgentReportResource`).
            UI'da qo'shimcha shart qo'yish operatorni server bergan
            ma'lumotdan mahrum qilardi — huquq modeli bitta joyda, backendda
            turishi kerak. --- */}
        <Col xs={24} xl={12}>
          <Card
            title={t('reports.agents.title')}
            extra={
              <ExportButton
                kind="agents"
                range={range}
                limit={AGENT_LIMIT}
                hasRows={agentRows.length > 0}
                enabled={canExport}
              />
            }
          >
            <CardSubtitle text={t('reports.agents.subtitle')} />

            {agents.isError ? (
              <SectionError error={agents.error} />
            ) : (
              <Table<AgentPerformance>
                columns={agentColumns}
                dataSource={agentRows}
                rowKey={(row) => row.agentId}
                loading={agents.isPending}
                size="small"
                pagination={false}
                scroll={{ x: 760 }}
                locale={emptyLocale(t('reports.agents.empty'), t('reports.agents.emptyHint'))}
              />
            )}

            {/* Yashirilganlar YO'QOLMAYDI — nechtaligi aytiladi */}
            {idleAgents > 0 ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('reports.agents.idleHidden', { count: idleAgents })}
              </Typography.Text>
            ) : null}
          </Card>
        </Col>
      </Row>

      {/* --- Qurilmalar.
          ⚠️ DAVR TANLOVIGA BOG'LIQ EMAS: bu bo'lim JORIY holatni sanaydi
          ("hozir nechta qurilma bor va ular qayerda"), oyna ichidagi
          iste'molni emas. Shuning uchun u yuqoridagi davr tanlovidan
          ATAYLAB ajratilgan va buni sarlavha ostidagi izoh aytadi — aks
          holda operator raqamlarni "30 kunlik" deb o'qib ketardi. --- */}
      <Card
        title={t('reports.devices.title')}
        style={{ marginTop: 16 }}
        extra={
          <ExportButton
            kind="devices"
            range={range}
            hasRows={deviceRows.length > 0}
            enabled={canExport}
          />
        }
      >
        <CardSubtitle text={t('reports.devices.subtitle')} />

        {devices.isError ? (
          <SectionError error={devices.error} />
        ) : (
          <>
            {/*
              ⚠️ "JOYLASHUVI KO'RSATILMAGAN" — bu bo'limning eng muhim
              raqami va shuning uchun jadvaldan YUQORIDA turadi.

              U ma'lumot sifatining o'lchovi: quyidagi viloyatlar kesimi
              aynan shu qurilmalarni HISOBGA OLMAYDI. Uni pastda kichik
              izoh qilib qo'yish jadvalni to'liq ko'rinishga o'xshatardi —
              va uzilish paytida "bu router qayerda?" degan savol aynan
              shu qurilmalar ustida paydo bo'ladi.
            */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} md={8}>
                <Card size="small" loading={devices.isPending}>
                  <Statistic
                    title={t('reports.devices.unplaced')}
                    value={unplacedTotal}
                    valueStyle={{ color: unplacedTotal > 0 ? '#d4380d' : undefined }}
                  />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('reports.devices.unplacedSplit', {
                      nas: unplacedNas,
                      equipment: unplacedEquipment,
                    })}
                  </Typography.Text>
                </Card>
              </Col>
              <Col xs={24} md={16}>
                {unplacedTotal > 0 ? (
                  <Alert
                    type="warning"
                    showIcon
                    message={t('reports.devices.unplacedWarnTitle')}
                    description={
                      <>
                        <div>{t('reports.devices.unplacedWarnText')}</div>
                        {/*
                          ⚠️ QAYSI QURILMA — endi ko'rinadi.

                          Ilgari faqat SON bor edi ("2 ta joylashuvsiz") va
                          operator qaysi qurilma ekanini bilish uchun NAS va
                          Jihozlar sahifalarini qo'lda ko'zdan kechirishi
                          kerak edi. Backend ro'yxatni allaqachon berardi,
                          panel esa uni umuman o'qimasdi.

                          Ro'yxat KESILGAN bo'lishi mumkin — shuning uchun
                          sanoq bilan solishtiriladi va farq aytiladi.
                        */}
                        {unplacedDevices.length > 0 ? (
                          <Space size={4} wrap style={{ marginTop: 8 }}>
                            {unplacedDevices.map((d) => (
                              <Tag key={`${d.kind}-${d.deviceId}`}>
                                {d.label ?? d.deviceId}
                                {d.detail ? ` · ${d.detail}` : ''}
                              </Tag>
                            ))}
                            {unplacedDevices.length < unplacedTotal ? (
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {t('reports.devices.unplacedTruncated', {
                                  shown: unplacedDevices.length,
                                  total: unplacedTotal,
                                })}
                              </Typography.Text>
                            ) : null}
                          </Space>
                        ) : null}
                      </>
                    }
                  />
                ) : (
                  <Alert
                    type="success"
                    showIcon
                    message={t('reports.devices.unplacedOkTitle')}
                    description={t('reports.devices.unplacedOkText')}
                  />
                )}
              </Col>
            </Row>

            <Table<DeviceRegionRow>
              columns={deviceColumns}
              dataSource={deviceRows}
              rowKey={(row) => row.region}
              loading={devices.isPending}
              size="small"
              pagination={false}
              scroll={{ x: 700, y: 360 }}
              locale={emptyLocale(
                t('reports.devices.empty'),
                t('reports.devices.emptyHint'),
              )}
            />

            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={24} md={12}>
                <Typography.Text strong>{t('reports.devices.byType')}</Typography.Text>
                <DeviceBreakdown
                  rows={devices.data?.byType ?? []}
                  prefix="equipmentType"
                  emptyText={t('reports.devices.noBreakdown')}
                />
              </Col>
              <Col xs={24} md={12}>
                <Typography.Text strong>{t('reports.devices.byState')}</Typography.Text>
                <DeviceBreakdown
                  rows={devices.data?.byState ?? []}
                  prefix="equipmentState"
                  emptyText={t('reports.devices.noBreakdown')}
                />
              </Col>
            </Row>
          </>
        )}
      </Card>

      {/* --- Batafsil eksport --- */}
      <DetailedExportCard
        range={range}
        enabled={canExport}
        subscriberTotal={summary.data?.totalSubscribers ?? null}
      />
    </>
  )
}

interface DetailedExportCardProps {
  range: ReportRange
  enabled: boolean
  /** Reyestr hajmining taxmini — chegara ogohlantirishi uchun */
  subscriberTotal: number | null
}

/**
 * EKRANDA JADVALI YO'Q, lekin serverda BOR bo'lgan ikkita eksport.
 *
 * <h2>⚠️ Nega alohida kartochka</h2>
 * `revenue-by` (daromad kesimi) va `subscribers` (abonentlar reyestri)
 * backendda allaqachon bor edi va panel ularni SO'RAMASDI. Ularni
 * yuqoridagi bo'limlarga qo'shib bo'lmadi: birinchisi kesim tanlashni
 * talab qiladi, ikkinchisi esa umuman vaqt qatori emas — u BATAFSIL
 * ro'yxat va faylda yuz mingta qator bo'lishi mumkin.
 *
 * <h2>⚠️ Reyestrdagi oyna `created_at` ga qo'llanadi</h2>
 * Ya'ni fayl "shu davrda RO'YXATGA OLINGAN abonentlar" — "shu davrda
 * faol bo'lganlar" EMAS. Bu farqni yozmaslik faylni ro'yxatning to'liq
 * nusxasi deb o'qitardi va yo'q abonentlar "o'chirilgan" deb
 * tushunilardi. Shuning uchun izoh tugmaning YONIDA turadi.
 */
function DetailedExportCard({ range, enabled, subscriberTotal }: DetailedExportCardProps) {
  const { t } = useTranslation()
  const [dimension, setDimension] = useState<RevenueDimension>('COST_CENTER')
  const [subState, setSubState] = useState<string | null>(null)
  const [subKind, setSubKind] = useState<string | null>(null)

  if (!enabled) return null

  return (
    <Card title={t('export.detailed.title')} style={{ marginTop: 16 }}>
      <CardSubtitle text={t('export.detailed.subtitle')} />

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Typography.Text strong>{t('export.detailed.revenueBy')}</Typography.Text>
          <div style={{ margin: '8px 0' }}>
            <Select<RevenueDimension>
              value={dimension}
              onChange={setDimension}
              style={{ minWidth: 220 }}
              options={REVENUE_DIMENSIONS.map((value) => ({
                value,
                label: t(`export.dimension.${value}`),
              }))}
            />
          </div>
          <ExportButton
            kind="revenue-by"
            range={range}
            hasRows
            enabled={enabled}
            dimension={dimension}
          />
        </Col>

        <Col xs={24} md={12}>
          <Typography.Text strong>{t('export.detailed.subscribers')}</Typography.Text>
          <div style={{ margin: '8px 0' }}>
            <Space wrap size="small">
              <Select<string>
                allowClear
                value={subState}
                onChange={(value) => setSubState(value ?? null)}
                placeholder={t('subscribers.columns.status')}
                style={{ minWidth: 150 }}
                options={SUBSCRIBER_STATES.filter((value) => value !== 'AVAILABLE').map((value) => ({
                  value,
                  label: t(`subscriberState.${value}`),
                }))}
              />
              <Select<string>
                allowClear
                value={subKind}
                onChange={(value) => setSubKind(value ?? null)}
                placeholder={t('subscribers.columns.kind')}
                style={{ minWidth: 140 }}
                options={DELIVERY_METHODS.map((value) => ({
                  value,
                  label: t(`deliveryMethod.${value}`),
                }))}
              />
            </Space>
          </div>
          <ExportButton
            kind="subscribers"
            range={range}
            hasRows
            enabled={enabled}
            estimatedRows={subscriberTotal}
            state={subState}
            subscriberKind={subKind}
          />
          <div style={{ marginTop: 8 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('export.detailed.registerNote', { from: range.from, to: range.to })}
            </Typography.Text>
          </div>
        </Col>
      </Row>
    </Card>
  )
}
